// ═══════════════════════════════════════════════════════════════════
// CardRenderer.js — THE ONE CARD FACE
// ═══════════════════════════════════════════════════════════════════
// caP'n's ruling (July 24, 2026): a card is a PRODUCT. It has ONE face.
// The booster, the Codex, the Vault, the Deck Builder, the 2D duel and
// the 3D Shatterfield all show the SAME face, because they all mount
// THIS COMPONENT. Not a port of it. Not a canvas painting that matches
// it. It.
//
// Before this file existed, the 3D board painted its own procedural
// frame (gold banner, art window, school·ilk line) and its own foils
// (nine hand-written WebGL recipes). Two renderers, two products, and
// every new tier cost double. That is deleted. The 3D card face is now
// this DOM, mounted in the world through <Html transform>.
//
// ── WHAT LIVES HERE ────────────────────────────────────────────────
//   • the gradient slab + border + gild
//   • the CDN art (objectFit: cover) and its legibility scrim
//   • <HoloFoil> — the real shared foil, all tiers, no exceptions
//   • the tier badge
//   • the type/level line, the name, the stat footer
//
// ── WHAT DOES NOT ──────────────────────────────────────────────────
// Anything that ANIMATES A STATE CHANGE: settle, shatter, lunge,
// recoil, damage floats, fallen veil, soul pips, deviant sigil. Those
// are the caller's — they differ per surface (the 3D board expresses a
// hit as a mesh shake, the 2D board as a CSS class) and they are not
// part of what the card IS.
//
// ── THE SCALE KNOB ─────────────────────────────────────────────────
// `k` multiplies every font/glyph size. The 2D board passes k=1 and
// renders byte-identical to what shipped before this file. The 3D board
// mounts a 220px-wide card and passes k=2.5 so the same layout reads at
// the same relative weight. NEVER re-author the layout for a surface —
// turn the knob.
// ═══════════════════════════════════════════════════════════════════
import React, { useEffect, useState } from "react";
import { SD, GM, TYPE_COLORS_SD, shimmerBorder } from "./theme";
import { StatChip } from "./StatGlyph";
import HoloFoil from "./HoloFoil";
import { HOLO_TIER_INFO } from "./PackArt";
import { cardUrl } from "./CDN";

// Marker class. Two jobs: a stable hook for CSS, and the liveness probe
// the 3D board uses to prove its DOM layer actually mounted
// (document.querySelectorAll('.tcard-face').length).
export const CARD_FACE_CLASS = "tcard-face";

// ═══════════════════════════════════════════════════════════════════
// PRIMERAS — THE STAMPS (H22b, July 2026)
// ═══════════════════════════════════════════════════════════════════
// Every Escendant bears two stamps in its top corners: its Ilk (top-left)
// and its School (top-right) — innate powers, of the flesh, not cards.
// Per the ONE FACE law the stamps live HERE, once, and every surface
// inherits them. They render ONLY when the caller passes a `stamps`
// prop, so all existing surfaces stay byte-identical until they opt in
// (the 2D duel opts in this wave; Codex/Vault/3D can turn the knob later).
//
// stamps = {
//   ilk:   { word, color, state, onPress },   // canon Ilk  — top-left
//   school: { word, color, state, onPress },   // canon School — top-right
// }
// state: "ready" (pressable, breathing) | "spent" (pressed-in, greyed)
//      | "locked" (dim — illegal right now; press still explains itself)
//
// The Ilk word is `card.ilk` (Humanoid/Beast/Machine/Dragon) and the
// School word is `card.school` (Space/Phantom/Dragon/Time). An axis
// inversion note stood here until 2026-08-25, when the two field names
// were swapped in cards.js to match the stamps; callers no longer
// translate anything. This component still just renders the words it
// is handed.
//
// Keyframes are injected imperatively into document.head — NEVER a
// <style> block in the component (terror-holofoil law: a component-tied
// sheet unmounts with the first instance and breaks the animation
// app-wide). The guard self-heals on textContent drift (stale-sheet law).
const STAMP_KEYFRAMES_ID = "tcard-stamp-keyframes";
const STAMP_KEYFRAMES_CSS = `
@keyframes tcardStampPunch {
  0%   { transform: scale(1) rotate(var(--stamp-rot, 0deg)); }
  28%  { transform: scale(1.55) rotate(var(--stamp-rot, 0deg)); }
  55%  { transform: scale(0.82) rotate(var(--stamp-rot, 0deg)); }
  75%  { transform: scale(1.12) rotate(var(--stamp-rot, 0deg)); }
  100% { transform: scale(1) rotate(var(--stamp-rot, 0deg)); }
}
@keyframes tcardStampRing {
  0%   { transform: scale(0.4); opacity: 0.9; }
  100% { transform: scale(2.6); opacity: 0; }
}
@keyframes tcardStampBreath {
  0%, 100% { box-shadow: 0 0 4px var(--stamp-glow), inset 0 1px 1px rgba(255,255,255,0.25), inset 0 -1px 2px rgba(0,0,0,0.55); }
  50%      { box-shadow: 0 0 9px var(--stamp-glow), 0 0 2px var(--stamp-glow), inset 0 1px 1px rgba(255,255,255,0.25), inset 0 -1px 2px rgba(0,0,0,0.55); }
}
`;
function ensureStampKeyframes() {
  if (typeof document === "undefined") return;
  const existing = document.getElementById(STAMP_KEYFRAMES_ID);
  if (existing) {
    // STALE-SHEET SELF-HEAL — an old deploy's sheet survives an id check;
    // replace on drift so new keyframes always resolve.
    if (existing.textContent !== STAMP_KEYFRAMES_CSS) existing.textContent = STAMP_KEYFRAMES_CSS;
    return;
  }
  const el = document.createElement("style");
  el.id = STAMP_KEYFRAMES_ID;
  el.textContent = STAMP_KEYFRAMES_CSS;
  document.head.appendChild(el);
}

// One wax-seal stamp. `side` = "left" | "right" (Ilk left, School right).
// SHIP 8 (2026-09-15): EXPORTED, unchanged. The 3D board's standing beings
// take the DOM face off their rift (a body cannot stand in front of a CSS3D
// decal — DOM paints over WebGL regardless of depth), so the two seals are
// mounted there as billboards over the card's own corners. Same component,
// same press path, same `stamps` object; ONE FACE still owns the stamp.
export function PrimeraStamp({ stamp, side, k, radius }) {
  const [punching, setPunching] = useState(false);
  useEffect(() => { ensureStampKeyframes(); }, []);
  if (!stamp || !stamp.word) return null;

  const px = (n) => Math.max(1, Math.round(n * k));
  const sz = px(17);
  const c = stamp.color || "#c8963c";
  const spent = stamp.state === "spent";
  const ready = stamp.state === "ready";
  const rot = side === "left" ? "-8deg" : "8deg";
  const glyph = (stamp.word || "?").charAt(0).toUpperCase();

  const handlePress = (e) => {
    e.stopPropagation(); // never open the slot radial underneath
    if (!spent) {
      setPunching(true);
      setTimeout(() => setPunching(false), 480);
    }
    stamp.onPress && stamp.onPress();
  };

  return (
    <div
      onClick={handlePress}
      title={stamp.word}
      style={{
        position: "absolute", top: px(13), zIndex: 4,
        [side]: px(3),
        width: sz, height: sz, borderRadius: "50%",
        cursor: "pointer",
        // ⚠ THE ONE POINTER-LIVE PIXEL (H22c, July 26). In 2D this is a
        // no-op — the whole face already takes clicks. In 3D it is the
        // entire press path: a CSS3D card face is a DECAL (its host div
        // and the CSS3D overlay both carry pointer-events:none so WebGL
        // picking sees the mesh underneath — the July 25 "click-eater").
        // A descendant with `auto` is still hit-testable under a `none`
        // ancestor, so THIS 39px circle, and nothing else on the card,
        // becomes a button. Deliberately re-introducing the click-eater
        // on exactly the region that IS a button. `stopPropagation` in
        // handlePress keeps the slot radial from opening underneath.
        pointerEvents: "auto",
        touchAction: "manipulation",
        // CSS vars drive the keyframes — per-stamp color + resting tilt
        "--stamp-rot": rot,
        "--stamp-glow": `${c}88`,
        transform: `rotate(${rot})`,
        background: spent
          ? `radial-gradient(circle at 35% 30%, #3a3a3a, #1c1c1c 70%)`
          : `radial-gradient(circle at 35% 30%, ${c}55, ${c}22 55%, rgba(0,0,8,0.85) 100%)`,
        border: `1px solid ${spent ? "#4a4a4a" : c}`,
        boxShadow: spent
          ? `inset 0 2px 4px rgba(0,0,0,0.85)` // pressed INTO the card
          : `0 0 4px ${c}66, inset 0 1px 1px rgba(255,255,255,0.25), inset 0 -1px 2px rgba(0,0,0,0.55)`,
        opacity: spent ? 0.45 : stamp.state === "locked" ? 0.55 : 1,
        display: "flex", alignItems: "center", justifyContent: "center",
        animation: punching
          ? "tcardStampPunch 0.48s cubic-bezier(0.2, 1.4, 0.4, 1)"
          : ready ? "tcardStampBreath 2.4s ease-in-out infinite" : "none",
        transition: "opacity 0.25s, box-shadow 0.25s, border-color 0.25s",
      }}
    >
      {/* inner seal ring */}
      <div style={{
        position: "absolute", inset: px(2), borderRadius: "50%",
        border: `1px solid ${spent ? "#3a3a3a" : `${c}77`}`, pointerEvents: "none",
      }} />
      {/* the word's initial, engraved */}
      <span style={{
        fontFamily: GM.font, fontWeight: 900, fontSize: px(9), lineHeight: 1,
        color: spent ? "#666" : "#fff",
        textShadow: spent ? "none" : `0 0 ${px(4)}px ${c}, 0 1px 2px rgba(0,0,0,0.9)`,
        pointerEvents: "none", userSelect: "none",
      }}>{glyph}</span>
      {/* ink ring burst on the punch */}
      {punching && (
        <div style={{
          position: "absolute", inset: 0, borderRadius: "50%",
          border: `2px solid ${c}`, pointerEvents: "none",
          animation: "tcardStampRing 0.48s ease-out forwards",
        }} />
      )}
    </div>
  );
}

// Tier badge text/colour derive from PackArt's registry — the one place
// tier metadata lives (terror-holofoil law). Nothing is retyped here.
const tierInfo = (holoTier) => {
  const t = holoTier ? HOLO_TIER_INFO[holoTier] : null;
  if (!t) return null;
  return { label: `${t.glyph} ${t.label.toUpperCase()}`, color: t.color };
};

export function CardFace({
  card,                    // resolved card object (caller owns the lookup)
  cardId = null,
  holoTier = null,
  width = 88,
  height = 122,
  k = 1,                   // font/glyph scale multiplier
  size = "normal",         // "normal" | "small" | "tiny" | "hand"
  mobile = false,
  radius = 10,
  onClick = null,
  className = "",          // ambient holo glow school (sf-holo-*) rides here
  highlight = false,
  fractured = false,
  attacked = false,
  intangible = false,
  flipped = false,
  displayLyf = null,
  displayLyt = null,
  lyfBuffed = false,
  lytBuffed = false,
  stamps = null,           // Primera stamps (H22b) — opt-in per surface
  style = {},
}) {
  const [imgFailed, setImgFailed] = useState(false);
  useEffect(() => { setImgFailed(false); }, [cardId]);

  if (!card) return null;

  const holo = tierInfo(holoTier);
  const typeColor = TYPE_COLORS_SD[card.type] || GM.vellumFaint;
  const imgUrl = cardId ? cardUrl(cardId) : null;

  const borderColor = highlight ? SD.gold
    : fractured ? SD.red
    : holo ? holo.color
    : typeColor + "99";

  // MAGNUM PASS 2 — gilded frame: faint gold inner hairline + a soft
  // top-light, composed under every state so the frame reads as metal.
  const gildInset = `inset 0 0 0 1px rgba(200,168,75,0.14), inset 0 14px 20px -14px rgba(255,240,200,0.18)`;
  const boxShadow = intangible
    ? `0 0 18px ${SD.purpleLight}99, inset 0 0 12px ${SD.purpleLight}33`
    : highlight ? `${shimmerBorder(SD.gold)}, ${gildInset}`
    : fractured ? `0 0 14px ${SD.red}55, ${gildInset}`
    : gildInset;

  const px = (n) => Math.max(1, Math.round(n * k));
  const tiny = size === "tiny";
  const bare = tiny || mobile;

  return (
    <div
      onClick={onClick}
      className={`${CARD_FACE_CLASS} ${className}`}
      style={{
        width, height, borderRadius: radius,
        background: fractured
          ? `linear-gradient(145deg, ${SD.redDim}, ${SD.void2})`
          : `linear-gradient(145deg, ${SD.void2}, ${SD.void1})`,
        border: `1px solid ${borderColor}`,
        display: "flex", flexDirection: "column",
        padding: `${px(8)}px ${px(9)}px`,
        cursor: onClick ? "pointer" : "default",
        boxShadow,
        overflow: "hidden", transition: "all 0.2s",
        opacity: attacked ? 0.5 : 1,
        fontFamily: GM.font, gap: px(4),
        // ── THE FOIL BLEND FIX ────────────────────────────────────────
        // `position:relative` makes THIS box the containing block for the
        // art layer + the foil. `isolation:isolate` makes it the blend
        // group: the foil's color-dodge / soft-light / multiply passes
        // composite against THE CARD ART instead of against nothing.
        position: "relative", isolation: "isolate",
        ...style,
      }}
    >
      {imgUrl && (
        <div style={{ position: "absolute", inset: 0, borderRadius: radius - 1, overflow: "hidden", zIndex: 0 }}>
          {!imgFailed && (
            <img
              src={imgUrl}
              alt={card.name}
              draggable={false}
              // NO crossOrigin — terror-assets sends no CORS headers, and a
              // crossOrigin-tagged <img> hard-fails there. A plain <img>
              // needs none (we never read its pixels). This one attribute
              // was the July 25 "dark slab" bug.
              onError={() => setImgFailed(true)}
              style={{ width: "100%", height: "100%", objectFit: "cover", display: "block", opacity: 0.92 }}
            />
          )}
          <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to bottom, rgba(0,0,5,0.55) 0%, rgba(0,0,5,0.1) 40%, rgba(0,0,5,0.75) 100%)" }} />
        </div>
      )}

      {/* THE FOIL. The shop's component, unmodified, on every surface.
          NO z-index — a positioned element with a z-index becomes its own
          stacking context, and a stacking context is an ISOLATION GROUP;
          every mix-blend-mode layer inside HoloFoil would then composite
          against the overlay's own transparent backdrop instead of the
          card art. With z-index:auto the overlay still paints ABOVE the
          art (both positioned, foil later in tree order) and BELOW the
          text (which carries z-index:1). Corner flourishes are suppressed
          below `normal`: gold curls on a 50px curio are clutter. */}
      {holoTier && holo && (
        <HoloFoil tier={holoTier} radius={radius - 1} corners={size === "normal" ? undefined : false} />
      )}

      {holo && (
        <div style={{
          position: "absolute", top: px(2), right: px(2), zIndex: 3,
          fontSize: px(7), color: holo.color, fontWeight: 900,
          letterSpacing: 0.5 * k, textShadow: `0 0 ${px(4)}px ${holo.color}`,
        }}>{holo.label}</div>
      )}

      {/* PRIMERAS — THE STAMPS (H22b). Ilk seals the top-left corner,
          School the top-right, sitting just below the type line / tier
          badge. Escendants only — Esclings bear none (R78). Rendered
          only when the surface opts in via the `stamps` prop. */}
      {stamps && card.type === "Escendant" && (
        <>
          <PrimeraStamp stamp={stamps.ilk}   side="left"  k={k} radius={radius} />
          <PrimeraStamp stamp={stamps.school} side="right" k={k} radius={radius} />
        </>
      )}

      <div style={{
        transform: flipped ? "rotate(180deg)" : "none",
        display: "flex", flexDirection: "column", gap: px(4),
        flex: 1, position: "relative", zIndex: 1,
      }}>
        <div style={{
          fontSize: px(mobile ? 8 : 10), color: imgUrl ? "#fff" : typeColor,
          letterSpacing: 1 * k, fontWeight: 900, lineHeight: 1,
          textShadow: imgUrl ? "0 1px 3px rgba(0,0,0,0.9)" : "none",
        }}>
          {card.type?.substring(0, 3).toUpperCase()}{card.level ? ` L${card.level}` : ""}
        </div>

        <div style={{
          fontSize: px(tiny ? (mobile ? 8 : 10) : (mobile ? 10 : 12)),
          color: imgUrl ? "#fff" : GM.vellumDim, fontStyle: "italic",
          lineHeight: 1.3, flex: 1, overflow: "hidden",
          textShadow: imgUrl ? "0 1px 4px rgba(0,0,0,0.95)" : "none",
        }}>
          {card.name}
        </div>

        {card.type === "Escendant" && card.lyt !== undefined && (() => {
          const gsz = px(tiny ? 9 : mobile ? 10 : 12);
          const gfs = px(tiny ? 10 : mobile ? 11 : 13);
          return (
            <div style={{ display: "flex", justifyContent: "space-between", fontWeight: 900 }}>
              <StatChip kind="lyf" value={displayLyf}
                color={fractured ? "#e05555" : (lyfBuffed ? "#f0d060" : "#4a9fd4")}
                size={gsz} fontSize={gfs} bare={bare} />
              <StatChip kind="lyt" value={displayLyt}
                color={lytBuffed ? "#f0d060" : "#e05555"}
                size={gsz} fontSize={gfs} bare={bare} />
            </div>
          );
        })()}

        {card.type === "Escling" && (
          <div style={{ display: "flex", justifyContent: "flex-end", fontWeight: 900 }}>
            <StatChip kind="lyt" value={card.lyt} color="#e05555"
              size={px(tiny ? 9 : mobile ? 10 : 12)}
              fontSize={px(tiny ? 10 : mobile ? 11 : 13)} bare={bare} />
          </div>
        )}

        {card.type !== "Escendant" && card.type !== "Escling" && (() => {
          const showLyf = ["Ritual", "Construction", "Treasure"].includes(card.ilk) && card.lyf != null;
          const isUhlti = card.type === "U'hlti";
          const gsz = px(tiny ? 8 : mobile ? 9 : 10);
          const gfs = px(tiny ? 9 : mobile ? 10 : 11);
          return (
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontWeight: 900 }}>
              {isUhlti
                ? <StatChip kind="anima" value={card.animaCost ?? 0} color={SD.red} size={gsz} fontSize={gfs} bare={bare} />
                : showLyf
                  ? <StatChip kind="lyf" value={displayLyf != null ? displayLyf : card.lyf}
                      color={fractured ? SD.red : SD.blue} size={gsz} fontSize={gfs} bare={bare} />
                  : <span />}
              {card.khaoCost != null
                ? <StatChip kind="khao" value={card.khaoCost} color={SD.gold} size={gsz} fontSize={gfs} bare={bare} />
                : <span />}
            </div>
          );
        })()}

        {card.effectTag && <div style={{ fontSize: px(9), color: SD.gold }}>{card.effectTag}</div>}
        {card.ssg && <div style={{ fontSize: px(9), color: SD.gold, fontWeight: 900, letterSpacing: 1 * k }}>◆ SSG</div>}
        {intangible && <div style={{ fontSize: px(9), color: SD.purpleLight, fontWeight: 900, letterSpacing: 1 * k }}>◇ INTANGIBLE</div>}
      </div>
    </div>
  );
}

export default CardFace;
